<template>
  <div class="c-kdo-fields">
    <n-loader :loading="$var('loadFields')" />

    <div class="kdo-toolbar">
      <input v-model="search" class="kdo-search" type="text" placeholder="Поиск по ключу или названию">
      <button type="button" class="kdo-btn primary" @click="openEditor(null)">+ Добавить поле</button>
    </div>

    <div class="kdo-card">
      <table>
        <thead>
          <tr>
            <th>Макрос</th>
            <th>Название</th>
            <th>Вид</th>
            <th v-if="companyId">Происхождение</th>
            <th>В шаблонах</th>
            <th>Статус</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filtered" :key="item.key">
            <td>
              <span class="kdo-macro">{{ item.macro }}</span>
              <span v-if="item.isSystem" class="lock" title="Системное поле: используется интеграцией">🔒</span>
            </td>
            <td>{{ item.label }}</td>
            <td>{{ workAsTitle(item.workAs) }}<span v-if="item.dataSource" class="sub"> · {{ sourceTitle(item.dataSource) }}</span></td>
            <td v-if="companyId">
              <span class="kdo-badge" :class="originClass(item.origin)">{{ originTitle(item.origin) }}</span>
            </td>
            <td>{{ item.usedIn }}</td>
            <td><span class="kdo-badge" :class="item.isActive ? 'ok' : 'muted'">{{ item.isActive ? 'Активно' : 'Отключено' }}</span></td>
            <td class="actions">
              <button type="button" class="kdo-btn small" @click="openEditor(item)">
                {{ companyId && item.origin === 'global' ? 'Своя версия' : 'Изменить' }}
              </button>
              <button
                v-if="companyId && item.origin === 'company'"
                type="button"
                class="kdo-btn small"
                title="Сделать поле доступным всем компаниям"
                @click="toPromote = item">
                В библиотеку
              </button>
              <button
                v-if="canDelete(item)"
                type="button"
                class="kdo-btn small danger"
                @click="toDelete = item">
                {{ item.origin === 'override' ? 'Вернуть библиотечное' : 'Удалить' }}
              </button>
            </td>
          </tr>
          <tr v-if="!filtered.length">
            <td :colspan="companyId ? 7 : 6" class="empty">Полей нет</td>
          </tr>
        </tbody>
      </table>
    </div>

    <c-kdo-field-editor
      v-if="editorOpen"
      :company-id="companyId"
      :field="editing"
      :dict="dict"
      :all-fields="rows"
      @saved="onSaved"
      @close="editorOpen = false" />

    <c-modal-message
      v-if="toPromote"
      :title="`Перенести поле «${toPromote.label}» в общую библиотеку?`"
      :description="promoteDescription"
      @agree="promote"
      @cancel="toPromote = null" />

    <c-modal-message
      v-if="toDelete"
      :title="deleteTitle"
      :description="deleteDescription"
      @agree="remove"
      @cancel="toDelete = null" />
  </div>
</template>

<script>
export default {
  name: 'CKdoFieldsTable',
  props: {
    companyId: { type: [ Number, String, ], default: null, },
  },
  data() {
    return {
      rows: [],
      dict: { workAs: [], valueTypes: [], dataSources: [], neededFieldsBySource: {}, },
      search: '',
      editorOpen: false,
      editing: null,
      toDelete: null,
      toPromote: null,
    }
  },
  computed: {
    filtered() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.rows
      return this.rows.filter((item) => item.key.toLowerCase().includes(query) || item.label.toLowerCase().includes(query))
    },
    promoteDescription() {
      return 'Поле станет доступно всем компаниям. У этой компании ничего не изменится, шаблоны продолжат работать. ' +
        'Если у другой компании уже есть своё поле с таким ключом, оно останется и перекроет библиотечное.'
    },
    deleteTitle() {
      if (!this.toDelete) return ''
      return this.toDelete.origin === 'override' ?
        `Вернуть библиотечное поле «${this.toDelete.label}»?` :
        `Удалить поле «${this.toDelete.label}»?`
    },
    deleteDescription() {
      return this.toDelete && this.toDelete.origin === 'override' ?
        'Своя версия поля будет удалена, компания снова будет использовать поле из библиотеки.' :
        'Это действие нельзя отменить. Поле, которое используется в шаблонах, удалить не получится.'
    },
  },
  watch: {
    companyId() {
      this.load()
    },
  },
  created() {
    this.loadDictionaries()
    this.load()
  },
  methods: {
    loadDictionaries() {
      $api.kdo.dictionaries().then((response) => {
        this.dict = response.data.content
      })
    },
    load() {
      this.$var('loadFields', true)
      $api.kdo.fields(this.companyId).then((response) => {
        this.rows = response.data.content || []
      }).finally(() => {
        this.$var('loadFields', false)
      })
    },
    openEditor(item) {
      this.editing = item
      this.editorOpen = true
    },
    onSaved() {
      this.editorOpen = false
      this.load()
    },
    canDelete(item) {
      if (this.companyId) return item.origin !== 'global'
      return !item.isSystem
    },
    remove() {
      const item = this.toDelete
      this.toDelete = null
      this.$var('loadFields', true)
      $api.kdo.deleteField(item.id).then(() => {
        this.load()
      }).catch(() => {
        this.$var('loadFields', false)
      })
    },
    promote() {
      const item = this.toPromote
      this.toPromote = null
      this.$var('loadFields', true)
      $api.kdo.promoteField(item.id).then(() => {
        this.load()
      }).catch(() => {
        this.$var('loadFields', false)
      })
    },
    workAsTitle(key) {
      const item = $n.find(this.dict.workAs, (workAs) => workAs.key === key)
      return item ? item.title : key
    },
    sourceTitle(key) {
      const item = $n.find(this.dict.dataSources, (source) => source.key === key)
      return item ? item.title : key
    },
    originTitle(origin) {
      return { global: 'Библиотека', override: 'Переопределено', company: 'Своё поле', }[origin] || origin
    },
    originClass(origin) {
      return { global: '', override: 'warn', company: 'ok', }[origin]
    },
  },
}
</script>

<style lang="scss" scoped>
@import './kdo.scss';

.lock {
  margin-left: 6px;
}

.sub {
  color: #8f88a8;
  font-size: 12px;
}

.actions {
  white-space: nowrap;
  text-align: right;

  .kdo-btn {
    margin-left: 6px;
  }
}
</style>
